/* eslint-disable react/jsx-props-no-spreading */
import React from "react";
import AutomationTimestampCell from "./AutomationTimestampCell";
import AvatarCell from "./AvatarCell";
import DataCell from "./DataCell";
import DateCell from "./DateCell";
import DeleteSignalCell from "./DeleteSignalCell";
import DropdownCell from "./DropdownCell";
import IconsGroupCell from "./IconsGroupCell";
import LabelCell from "./LabelCell";
import LastTimeEntry from "./LastTimeEntry";
import LocationCell from "./LocationCell";
import MemberCell from "./MemberCell";
import MetricCell from "./MetricCell";
import PercentCell from "./PercentCell";
import PlaybookActionsCell from "./PlaybookActionsCell";
import RelatedAccountsCell from "./RelatedAccountsCell";
import SignalActivityCell from "./SignalActivityCell";
import SignalNameCell from "./SignalNameCell";
import SignalOwnerCell from "./SignalOwnerCell";
import SignalStatusCell from "./SignalStatusCell";
import SourceCell from "./SourceCell";
import StatusCell from "./StatusCell";
import TopFeatureCell from "./TopFeatureCell";

export type LegacyColumn = {
  key: string;
  title?: string;
  dataType?: string;
  isSortable?: boolean;
  sortType?: string;
  attributeType?: string;
  displayFormat?: string | null;
  generationType?: string | null;
  isLabelField?: boolean;
  labelOptions?: Array<{ value: string; display_name: string; color: string }>;
  colGroup?: {
    style?: {
      width?: number;
    };
  };
};

const DATE_TIME_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{6}$/;

const buildLegacyCellProps = (column: LegacyColumn, rowData: any) => ({
  column,
  rowData,
  dispatch: () => {},
  rowKeyValue: rowData?.randomId || rowData?.id,
});

export const renderLegacyCellContent = (column: LegacyColumn, rowData: any) => {
  const value = rowData?.[column.key];
  const props = buildLegacyCellProps(column, rowData);

  if (column.dataType === "delete_signal") return <DeleteSignalCell {...(props as any)} />;
  if (column.dataType === "playbook_actions") return <PlaybookActionsCell {...(props as any)} />;

  if (value === "null" || value === null || value === undefined) {
    return <span className="block h-[5px] bg-gray-200 w-[40px] rounded-lg" />;
  }

  // Label fields (e.g. lifecycle_stage) render as colored chips. This must come
  // before the metric_traits branch below, since label fields are computed_traits
  // (attribute_type "metric_traits") and would otherwise render as a plain MetricCell.
  if (column.isLabelField && column.labelOptions) {
    return <LabelCell value={value} options={column.labelOptions} />;
  }

  if (column.key === "related_accounts") return <RelatedAccountsCell {...(props as any)} />;
  if (column.dataType === "signal_name") return <SignalNameCell {...(props as any)} />;
  if (column.dataType === "signal_status") return <SignalActivityCell {...(props as any)} />;
  if (column.dataType === "last_activity") return <LastTimeEntry {...(props as any)} />;
  if (column.dataType === "top_feature") return <TopFeatureCell {...(props as any)} />;
  if (column.key === "send_via") return <IconsGroupCell {...(props as any)} />;
  if (column.dataType === "automation_timestamp") return <AutomationTimestampCell {...(props as any)} />;
  if (column.key === "status") return <StatusCell {...(props as any)} />;
  if (column.key === "sr.status") return <SignalStatusCell {...(props as any)} />;
  if (column.key === "sr.owner_id") return <SignalOwnerCell {...(props as any)} />;

  if (column.key.includes("retention") || column.key.includes("adoption")) {
    return <PercentCell {...(props as any)} />;
  }

  if (column.attributeType === "metric_traits") {
    // Computed trait datetime fields should render as dates, not metrics
    if (column.dataType === "datetime") return <DateCell {...(props as any)} />;
    return <MetricCell {...(props as any)} />;
  }

  if (column.key === "accounts.csm_id"
    || column.key === "accounts.ae_id"
    || column.key === "accounts.ae_updated_by_user_id"
    || column.key === "accounts.csm_updated_by_user_id") {
    return <MemberCell {...(props as any)} />;
  }

  if (column.key === "users.name"
    || column.key === "accounts.name"
    || column.key === "users.external_id"
    || column.key === "accounts.external_id") {
    return <AvatarCell {...(props as any)} />;
  }

  if (column.key === "location") return <LocationCell {...(props as any)} />;

  if (column.key.includes("source") || column.key === "src") {
    return <SourceCell {...(props as any)} />;
  }

  if (column.dataType === "object") return <DropdownCell {...(props as any)} />;

  if (column.dataType === "date") return <DateCell {...(props as any)} />;

  if (typeof value === "string" && value.match(DATE_TIME_REGEX)) {
    return <DateCell {...(props as any)} />;
  }

  return <DataCell {...(props as any)} />;
};

export const createLegacyTanStackColumns = (legacyColumns: LegacyColumn[]) => legacyColumns.map((legacyColumn) => ({
  id: legacyColumn.key,
  accessorFn: (row: any) => row?.[legacyColumn.key],
  header: legacyColumn.title || "-",
  enableSorting: !!legacyColumn.isSortable,
  size: legacyColumn?.colGroup?.style?.width,
  meta: {
    legacyColumn,
  },
  cell: ({ row }: any) => renderLegacyCellContent(legacyColumn, row.original),
}));
